import { forwardRef, Fragment } from "react";

import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { withStyles, WithStyles } from "@mui/styles";
import map from "lodash/map";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import { LANGUAGE_ID } from "../../../../shared/lang/lang.constants";

import { logout } from "../../../../common/services/auth/Auth.service";
import { FarmSettingsTo } from "../../../../generated/api/agroevidence";
import { AdminIcon } from "../../../../shared/icons/navbar/AdminIcon";
import { ChangePasswordIcon } from "../../../../shared/icons/navbar/ChangePasswordIcon";
import { FarmIcon } from "../../../../shared/icons/navbar/FarmIcon";
import { ImpersonationIcon } from "../../../../shared/icons/navbar/ImpersonationIcon";
import { IntegrationsIcon } from "../../../../shared/icons/navbar/IntegrationsIcon";
import { LocalizationIcon } from "../../../../shared/icons/navbar/LocalizationIcon";
import { LogoutIcon } from "../../../../shared/icons/navbar/LogoutIcon";
import { ProfileIcon } from "../../../../shared/icons/navbar/ProfileIcon";
import { UpdateLpisIcon } from "../../../../shared/icons/navbar/UpdateLpisIcon";
import Localization, {
  LANGUAGES,
} from "../../../../shared/services/Localization.service";
import { UserType } from "../../services/menuItems";
import NavbarService from "../../services/NavbarService";
import { prefetchModule } from "../../services/prefetch";
import CollapseSwitchMenu from "../CollapseSwitchMenu/CollapseSwitchMenu";
import { ContextMenuItem } from "../ContextMenuItem/ContextMenuItem";
import { UserAvatar } from "../UserAvatar/UserAvatar";

interface Farm {
  id: string;
  name: string;
  countryCode?: string;
}

interface NgProps {
  farm: Farm;
  farmSettings: FarmSettingsTo;
  farms: Farm[];
  isAdmin: boolean;
  langId: LANGUAGE_ID;
  langs: typeof LANGUAGES;
  user: UserType;
}

interface UserMenuProps extends WithStyles<typeof styles> {
  mobile?: boolean;
  ngProps: NgProps;
  onImpersonationToggle: () => void;
  onMenuItemClick?: () => void;
  updatePosition?: () => void;
}

const UserMenu = forwardRef<HTMLDivElement, UserMenuProps>((props, ref) => {
  const {
    classes,
    mobile = false,
    ngProps,
    onImpersonationToggle,
    onMenuItemClick = () => {},
    updatePosition = () => {},
  } = props;
  const { farm, farmSettings, farms, isAdmin, langId, langs, user } = ngProps;
  const username = NavbarService.getUsername(user);

  return (
    <div data-testid="user-menu" ref={ref}>
      <ListItem
        className={classes.userHeader}
        dense={true}
        classes={{
          gutters: mobile ? classes.mobileGutters : "",
        }}
      >
        <ListItemIcon
          classes={{
            root: classes.MuiListItemIcon_root,
          }}
        >
          <UserAvatar grey={true} user={user} />
        </ListItemIcon>
        <ListItemText
          primary={username}
          secondary={farm.name}
          secondaryTypographyProps={{ id: "user-menu-farmname" }}
          primaryTypographyProps={{
            variant: "subtitle2",
            id: "user-menu-username",
          }}
        />
      </ListItem>
      <hr
        className={mobile ? classes.mobileSeparator : classes.desktopSeparator}
      />
      <CollapseSwitchMenu
        currentItemId={farm.id}
        icon={<FarmIcon />}
        items={farms.sort((a, b) => a.name.localeCompare(b.name))}
        label={<FormattedMessage id="UserMenu.farm" />}
        mobile={mobile}
        switchId="farm"
        onMenuItemClick={(newFarmId: string) => {
          window.location.assign(`${window.location.origin}/farm/${newFarmId}`);
          setTimeout(() => {
            updatePosition();
          }, 0);
        }}
      />
      <CollapseSwitchMenu
        currentItemId={langId}
        icon={<LocalizationIcon />}
        label={<FormattedMessage id="UserMenu.language" />}
        mobile={mobile}
        switchId="language"
        items={map(langs, (lang, langKey) => ({
          id: langKey,
          name: lang.NAME,
        }))}
        onMenuItemClick={(newLangId: string) => {
          Localization.setLang(newLangId as LANGUAGE_ID);
          setTimeout(() => {
            updatePosition();
          }, 0);
        }}
      />
      <hr
        className={mobile ? classes.mobileSeparator : classes.desktopSeparator}
      />
      <ContextMenuItem
        component={Link}
        icon={<IntegrationsIcon />}
        id="user-menu-integrations"
        label={<FormattedMessage id="UserMenu.myIntegrations" />}
        mobile={mobile}
        onClick={onMenuItemClick}
        to={`/farm/${farm.id}/user/integrations`}
      />
      <ContextMenuItem
        component={Link}
        icon={<ProfileIcon />}
        id="user-menu-profile"
        label={<FormattedMessage id="UserMenu.myProfile" />}
        mobile={mobile}
        onClick={onMenuItemClick}
        to={`/farm/${farm.id}/user/profile`}
      />
      {farmSettings.eagriSettingsEnabled && (
        <ContextMenuItem
          component={Link}
          icon={<UpdateLpisIcon />}
          id="user-menu-update-lpis"
          label={<FormattedMessage id="UserMenu.updateLpis" />}
          mobile={mobile}
          onClick={onMenuItemClick}
          to={`/farm/${farm.id}/user/updatelpis`}
        />
      )}
      {farmSettings.shpSettingsEnabled && (
        <ContextMenuItem
          component={Link}
          icon={<UpdateLpisIcon />}
          id="user-menu-update-shp"
          label={<FormattedMessage id="UserMenu.updateShp" />}
          mobile={mobile}
          onClick={onMenuItemClick}
          to={`/farm/${farm.id}/user/updateshp`}
        />
      )}
      <ContextMenuItem
        component={Link}
        icon={<ChangePasswordIcon />}
        id="user-menu-password"
        label={<FormattedMessage id="UserMenu.changePassword" />}
        mobile={mobile}
        onClick={onMenuItemClick}
        to={`/farm/${farm.id}/user/password`}
      />
      {isAdmin && (
        <ContextMenuItem
          component={Link}
          icon={<AdminIcon />}
          id="user-menu-admin"
          label={<FormattedMessage id="UserMenu.admin" />}
          mobile={mobile}
          onClick={onMenuItemClick}
          onFocus={() => prefetchModule("admin")}
          onMouseEnter={() => prefetchModule("admin")}
          to={`/farm/${farm.id}/admin/farms`}
        />
      )}
      {isAdmin && (
        <ContextMenuItem
          icon={<ImpersonationIcon />}
          id="user-menu-impersonation"
          label={<FormattedMessage id="UserMenu.impersonation" />}
          mobile={mobile}
          onClick={() => {
            onMenuItemClick();
            onImpersonationToggle();
          }}
        />
      )}
      {!mobile && (
        <Fragment>
          <hr
            className={
              mobile ? classes.mobileSeparator : classes.desktopSeparator
            }
          />
          <ContextMenuItem
            icon={<LogoutIcon />}
            id="user-menu-logout"
            label={<FormattedMessage id="UserMenu.logout" />}
            mobile={mobile}
            onClick={logout}
          />
        </Fragment>
      )}
    </div>
  );
});

UserMenu.displayName = "UserMenu";

const styles = {
  desktopSeparator: {
    margin: "8px 0px",
  },
  mobileSeparator: {
    margin: "16px 0px",
  },
  userHeader: {
    paddingLeft: 16,
    paddingRight: 16,
  },
  username: {
    fontWeight: 500,
  },
  mobileGutters: {
    paddingLeft: 32,
    paddingRight: 32,
  },
  MuiListItemIcon_root: {
    minWidth: "unset",
  },
};

export default withStyles(styles)(UserMenu);
